// Shared rules for when a recording is eligible for a manual "Retry processing".
// Used by both the recordings list (RecordingHistory) and the detail view (MyScribes).

export const RETRYABLE_FAILURE_STATUSES = [
  "failed",
  "processing_error",
  "upload_failed",
  "upload_verification_failed",
  "download_failed",
  "download_failed_zero_size",
];

// Normal flow leaves "uploaded" within seconds - older than this means stuck
export const STALE_UPLOADED_MS = 5 * 60 * 1000;

// "Processing"/"transcribing" with no progress update for this long means stuck
export const STALE_PROCESSING_MS = 30 * 60 * 1000;

const STALE_PROCESSING_STATUSES = ["processing", "transcribing"];

const COMPLETED_STATUSES = ["completed", "analyzed"];

export interface RetryEligibilityParams {
  status: string | null | undefined;
  // Recording `date` (or created_at) - staleness baseline when no live updates seen
  recordingDate: string | null | undefined;
  // Epoch ms of the last progress/WebSocket update seen for this recording, if any
  lastActivityAt?: number | null;
  notes?: any;
  now?: number;
}

export function isRetryEligible(params: RetryEligibilityParams): boolean {
  const status = (params.status || "").toLowerCase();
  if (!status) return false;
  if (COMPLETED_STATUSES.includes(status)) return false;
  if (params.notes != null) return false;

  if (RETRYABLE_FAILURE_STATUSES.includes(status)) return true;

  const now = params.now ?? Date.now();
  const recordingTime = params.recordingDate
    ? new Date(params.recordingDate).getTime()
    : NaN;

  if (status === "uploaded") {
    return !Number.isNaN(recordingTime) && now - recordingTime > STALE_UPLOADED_MS;
  }

  if (STALE_PROCESSING_STATUSES.includes(status)) {
    const baseline = params.lastActivityAt ?? recordingTime;
    return !Number.isNaN(baseline) && now - baseline > STALE_PROCESSING_MS;
  }

  return false;
}

export const NO_TEMPLATE_MESSAGE =
  "No template attached — open the recording and pick a template first.";

export const RETRY_FAILED_MESSAGE = "Retry failed — please try again";
